import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";
import { PRIVACY_LAST_UPDATED, privacyPolicyContent } from "@/content/privacyPolicyContent";

export default function PrivacyPolicyPage() {
  const { language } = useTranslation();
  const { isDark } = useTheme();
  const copy = privacyPolicyContent[language === "es" ? "es" : "en"];
  const lastUpdatedLabel = language === "es" ? "Última actualización" : "Last updated";

  const pageBg = isDark ? "bg-[#050505]" : "bg-[#FAFAFA]";
  const titleCls = isDark ? "text-white" : "text-gray-900";
  const mutedCls = isDark ? "text-white/60" : "text-gray-600";
  const bodyCls = isDark ? "text-gray-300" : "text-gray-700";
  const accentCls = isDark ? "text-blue-400" : "text-red-600";
  const cardCls = isDark
    ? "bg-[#0A0A0A] border border-white/10"
    : "bg-white border border-gray-200 shadow-sm";

  return (
    <div className={`min-h-screen transition-colors duration-300 ${pageBg}`} data-testid="privacy-policy-page">
      <Navbar />
      <div className="pt-28 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <span className={`text-sm uppercase tracking-widest font-medium mb-3 block ${accentCls}`}>
              {copy.tagline}
            </span>
            <h1 className={`font-unbounded text-3xl md:text-4xl font-bold mb-3 ${titleCls}`}>
              {copy.title}
            </h1>
            <p className={`text-sm font-['Manrope'] mb-8 ${mutedCls}`}>
              {lastUpdatedLabel}: {PRIVACY_LAST_UPDATED}
            </p>
            <div className={`${cardCls} p-6 md:p-8 space-y-8`}>
              <p className={`text-sm md:text-base leading-relaxed font-['Manrope'] ${bodyCls}`}>
                {copy.intro}
              </p>
              <div className={`text-sm font-['Manrope'] leading-relaxed ${bodyCls}`}>
                {copy.dealerBlock.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>

              {copy.sections.map((section) => (
                <section key={section.title}>
                  <h2 className={`font-unbounded text-lg font-semibold mb-3 ${titleCls}`}>
                    {section.title}
                  </h2>
                  {(section.paragraphs || []).map((p) => (
                    <p key={p.slice(0, 48)} className={`text-sm leading-relaxed font-['Manrope'] mb-3 ${bodyCls}`}>
                      {p}
                    </p>
                  ))}
                  {section.bullets?.length > 0 && (
                    <ul className={`list-disc pl-5 space-y-2 text-sm font-['Manrope'] ${bodyCls}`}>
                      {section.bullets.map((item) => (
                        <li key={item.slice(0, 48)}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {section.after && (
                    <p className={`text-sm leading-relaxed font-['Manrope'] mt-3 ${bodyCls}`}>
                      {section.after}
                    </p>
                  )}
                </section>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
